import { useMemo } from 'react';
import type { SiteAnalysisResponse } from '@/lib/api.types';
import type { StatusKind } from '@/components/ui/StatusRow/StatusRow';
import { rankToStatus, rankToStatusLabel } from '@/lib/risk';
import { buildVerdict, STATUS_SYMBOL } from '@/lib/verdict';
import { scrollToId } from '@/lib/scroll';
import styles from './VerdictCard.module.css';

type Props = {
  analysis: SiteAnalysisResponse;
};

const STATUS_CLASS: Record<StatusKind, string> = {
  safe: styles.safe,
  alert: styles.alert,
  neutral: styles.neutral,
};

// Top-of-report BLUF: gives a non-expert buyer the bottom-line ("該当なし / 要確認 /
// 要注意") and a per-area breakdown before they read 8 chapters. Deliberately framed
// as guidance, not a verdict — the disclaimer keeps it consistent with the product's
// "not a substitute for professional judgement" positioning.
export default function VerdictCard({ analysis }: Props) {
  const { overall, status, dims, message } = useMemo(
    () => buildVerdict(analysis),
    [analysis],
  );

  return (
    <div className={styles.card} role="region" aria-label="この土地のレポート（総括）">
      <div className={styles.eyebrow}>この土地のレポート</div>
      <div className={styles.head}>
        <span className={`${styles.badge} ${STATUS_CLASS[status]}`}>
          <span className={styles.badgeSymbol} aria-hidden="true">
            {STATUS_SYMBOL[status]}
          </span>
          {rankToStatusLabel(overall, '判定中')}
        </span>
        <p className={styles.message}>{message}</p>
      </div>

      <ul className={styles.dims}>
        {dims.map((d) => {
          const s = rankToStatus(d.rank);
          return (
            <li key={d.key} className={`${styles.dim} ${STATUS_CLASS[s]}`}>
              <span className={styles.dimSymbol} aria-hidden="true">
                {STATUS_SYMBOL[s]}
              </span>
              <span className={styles.dimLabel}>{d.label}</span>
              <span className={styles.dimRank}>{rankToStatusLabel(d.rank, '—')}</span>
            </li>
          );
        })}
      </ul>

      <div className={styles.footer}>
        <button
          type="button"
          className={styles.jump}
          onClick={() => scrollToId('summary')}
        >
          くわしくは「まとめ」へ
          <span aria-hidden="true">→</span>
        </button>
        <p className={styles.disclaimer}>
          ※このレポートは重要事項説明や専門家の判断に代わるものではありません。
        </p>
      </div>
    </div>
  );
}
